import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheckIcon, ChartBarIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'
import Button from '../components/ui/Button'

const MAX_ATTEMPTS  = 5
const LOCKOUT_MS    = 5 * 60 * 1000  // 5 minutes

const FEATURES = [
  { icon: ShieldCheckIcon, text: 'Seguridad y control de accesos de nivel empresarial' },
  { icon: ChartBarIcon,    text: 'Reportes e indicadores de negocio en tiempo real' },
  { icon: UserGroupIcon,   text: 'Todas tus áreas y equipos en una sola plataforma' },
]

export default function Login() {
  const { login } = useAuth()

  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState('')
  const [loading,  setLoading]  = useState(false)
  const attemptsRef = useRef({ count: 0, lockUntil: 0 })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    const now = Date.now()
    if (attemptsRef.current.lockUntil > now) {
      const secs = Math.ceil((attemptsRef.current.lockUntil - now) / 1000)
      const mins = Math.floor(secs / 60)
      const s    = secs % 60
      setError(`Cuenta bloqueada por demasiados intentos. Espera ${mins}:${String(s).padStart(2,'0')} min.`)
      return
    }

    if (!email.trim())    { setError('Ingresa tu correo electrónico.');  return }
    if (!password)        { setError('Ingresa tu contraseña.');           return }

    setLoading(true)
    const { error: err } = await login({ email, password })
    setLoading(false)

    if (err) {
      attemptsRef.current.count++
      if (attemptsRef.current.count >= MAX_ATTEMPTS) {
        attemptsRef.current.lockUntil = Date.now() + LOCKOUT_MS
        attemptsRef.current.count = 0
        setError('Demasiados intentos fallidos. Acceso bloqueado por 5 minutos.')
        return
      }
      const remaining = MAX_ATTEMPTS - attemptsRef.current.count
      const msg = err.message ?? ''
      if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        setError(`Correo o contraseña incorrectos. (${remaining} intento${remaining !== 1 ? 's' : ''} restante${remaining !== 1 ? 's' : ''})`)
      } else if (msg.includes('Email not confirmed')) {
        setError('Debes confirmar tu correo antes de iniciar sesión.')
      } else if (msg.includes('Too many requests')) {
        setError('Demasiados intentos. Espera unos minutos e intenta de nuevo.')
      } else {
        setError(err.message)
      }
    } else {
      attemptsRef.current = { count: 0, lockUntil: 0 }
    }
    // Si no hay error: isAuthenticated se vuelve true → PublicRoute redirige a /dashboard
  }

  return (
    <div className="login-page">
      {/* ── Columna izquierda: marca ──────────────────────────── */}
      <div className="login-left">
        <div className="login-left-pattern" aria-hidden="true" />
        <div className="login-left-glow login-left-glow-1" aria-hidden="true" />
        <div className="login-left-glow login-left-glow-2" aria-hidden="true" />

        <div className="login-left-content">
          <img src="/logo-blanco.png" alt="KontrolSuite" className="login-brand-logo" />

          <h1 className="login-headline">Gestión empresarial inteligente.</h1>
          <p className="login-subheadline">
            Centraliza personas, procesos y datos de tu empresa en una sola plataforma
            diseñada para crecer contigo.
          </p>

          <ul className="login-features">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="login-feature-item">
                <span className="login-feature-icon"><Icon /></span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="login-left-footer">© {new Date().getFullYear()} KontrolSuite</div>
      </div>

      {/* ── Columna derecha: formulario ───────────────────────── */}
      <div className="login-right">
        {/* Tarjeta única — estructura DOM estable, no cambia forma ni tamaño */}
        <div className="login-card animate-fadeIn">
          <img src="/logo-negro.png" alt="KontrolSuite" className="login-card-logo" />

          <div className="login-card-heading">
            <h2>Bienvenido de nuevo</h2>
            <span>Inicia sesión para continuar en tu cuenta</span>
          </div>

          {/*
            noValidate: desactiva validación nativa del browser que puede
            inyectar nodos extra en el DOM y desencadenar insertBefore.
            Estructura fija: input+label por campo, banner de error y botón,
            sin nodos insertados/eliminados condicionalmente entre hermanos.
            El label flotante usa :placeholder-shown (solo CSS, sin estado
            React), así el DOM nunca cambia de forma entre renders.
          */}
          <form onSubmit={handleSubmit} noValidate className="login-form">
            <div className="login-field">
              <input
                id="login-email"
                type="email"
                className="login-input"
                placeholder=" "
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
              />
              <label htmlFor="login-email" className="login-label">Correo electrónico</label>
            </div>

            <div className="login-field">
              <input
                id="login-password"
                type="password"
                className="login-input"
                placeholder=" "
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <label htmlFor="login-password" className="login-label">Contraseña</label>
            </div>

            {/*
              Banner de error: SIEMPRE en el DOM (display toggling).
              Si usáramos {error && <div>} React haría insertBefore entre
              el input y el botón, fallando si alguna extensión modificó
              la lista de hijos del formulario.
            */}
            <div
              role="alert"
              aria-live="polite"
              className="login-error-banner"
              style={{ display: error ? 'block' : 'none' }}
            >
              {error}
            </div>

            <Button type="submit" loading={loading} className="login-submit">
              Iniciar sesión
            </Button>
          </form>

          {/* ── Link a registro ─────────────────────────────────── */}
          {/*
            Separado del <form> para evitar cualquier interferencia.
            Dos <span> independientes en lugar de texto + <Link> dentro
            de <p>, lo que generaba text nodes adyacentes al nodo React.
          */}
          <div className="login-register-link">
            <span>¿No tienes cuenta?</span>
            <Link to="/register">Regístrate gratis</Link>
          </div>
        </div>
      </div>
    </div>
  )
}
